import React from 'react';
import { 
  Lightbulb, 
  PenTool, 
  Code, 
  Rocket, 
  TrendingUp, 
  Settings, 
  RefreshCw,
  Users,
  CheckCircle,
  FileText,
  BarChart2,
  Database
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const ProductLifecycle = () => {
  const lifecyclePhases = [
    {
      stage: "1. Idéation",
      icon: Lightbulb,
      description: "Exploration et validation du concept",
      activities: [
        "Analyse des besoins du marché africain",
        "Étude des solutions existantes",
        "Validation avec clients potentiels"
      ],
      deliverables: [
        "Document d'analyse de marché",
        "Proposition de valeur unique",
        "Business model canvas"
      ],
      kpis: [
        "Taille du marché adressable",
        "Score de validation client",
        "Niveau d'innovation"
      ]
    },
    {
      stage: "2. Conception",
      icon: PenTool,
      description: "Design et architecture de la solution",
      activities: [
        "Architecture technique détaillée",
        "Design UX/UI adapté",
        "Prototypage interactif"
      ],
      deliverables: [
        "Documentation architecture",
        "Maquettes UI/UX",
        "Prototype fonctionnel"
      ],
      kpis: [
        "Complétude des spécifications",
        "Feedback utilisateurs",
        "Score d'accessibilité"
      ]
    },
    {
      stage: "3. Développement MVP",
      icon: Code,
      description: "Construction version minimale",
      activities: [
        "Développement agile",
        "Tests continus",
        "Intégration retours clients"
      ],
      deliverables: [
        "Code source documenté",
        "Tests automatisés",
        "Documentation technique"
      ],
      kpis: [
        "Vélocité de développement",
        "Couverture de tests",
        "Dette technique"
      ]
    },
    {
      stage: "4. Lancement",
      icon: Rocket,
      description: "Mise sur le marché",
      activities: [
        "Déploiement progressif",
        "Formation utilisateurs",
        "Support renforcé"
      ],
      deliverables: [
        "Plan de déploiement",
        "Matériel de formation",
        "Documentation utilisateur"
      ],
      kpis: [
        "Taux d'adoption",
        "Tickets support",
        "Satisfaction client"
      ]
    },
    {
      stage: "5. Croissance",
      icon: TrendingUp,
      description: "Expansion et amélioration",
      activities: [
        "Nouvelles fonctionnalités",
        "Optimisation performance",
        "Expansion géographique"
      ],
      deliverables: [
        "Roadmap produit",
        "Rapports de performance",
        "Plan d'expansion"
      ],
      kpis: [
        "Croissance utilisateurs",
        "Rétention client",
        "Performance système"
      ]
    },
    {
      stage: "6. Maturité",
      icon: Settings,
      description: "Optimisation et stabilisation",
      activities: [
        "Amélioration continue",
        "Automatisation processus",
        "Réduction coûts"
      ],
      deliverables: [
        "Tableaux de bord",
        "Processus optimisés",
        "Documentation maintenance"
      ],
      kpis: [
        "Coût par utilisateur",
        "Temps de réponse",
        "ROI client"
      ]
    },
    {
      stage: "7. Évolution",
      icon: RefreshCw,
      description: "Transformation ou renouveau",
      activities: [
        "Analyse nouvelles opportunités",
        "Refonte technologique",
        "Innovation continue"
      ],
      deliverables: [
        "Plan de modernisation",
        "Nouvelles architectures",
        "Stratégie d'évolution"
      ],
      kpis: [
        "Innovation score",
        "Adoption nouvelles features",
        "Satisfaction long terme"
      ]
    }
  ];

  const qualityGates = [
    {
      phase: "Technique",
      criteria: [
        "Tests automatisés > 80%",
        "Zéro vulnérabilité critique",
        "Performance optimale",
        "Documentation complète"
      ]
    },
    {
      phase: "Fonctionnel",
      criteria: [
        "Validation utilisateurs",
        "Tests d'acceptation",
        "Conformité spécifications",
        "Accessibilité"
      ]
    },
    {
      phase: "Business",
      criteria: [
        "ROI validé",
        "Alignement stratégique",
        "Feedback marché positif",
        "Plan support validé"
      ]
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <h2 className="text-2xl font-bold text-center text-white">
            Cycle de Vie Produit KG TECHNOLOGIES
          </h2>
          <p className="text-center text-white mt-2 opacity-90">
            De l'idéation à l'évolution continue
          </p>
        </div>
        
        <div className="p-6 space-y-8">
          {/* Phases du cycle de vie */}
          <div className="space-y-6">
            {lifecyclePhases.map((phase, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white mr-4">
                    <phase.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-semibold text-blue-600">{phase.stage}</h3>
                      <span className="text-sm text-gray-500">Phase {index + 1}/7</span>
                    </div>
                    <p className="text-gray-600 mb-4">{phase.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Activités */}
                      <div className="space-y-2">
                        <h4 className="font-medium flex items-center text-gray-700">
                          <Users className="w-4 h-4 mr-2" />
                          Activités
                        </h4>
                        <ul className="space-y-1">
                          {phase.activities.map((activity, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-600">
                              <span className="text-blue-500 mr-2">•</span>
                              {activity}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Livrables */}
                      <div className="space-y-2">
                        <h4 className="font-medium flex items-center text-gray-700">
                          <FileText className="w-4 h-4 mr-2" />
                          Livrables
                        </h4>
                        <ul className="space-y-1">
                          {phase.deliverables.map((deliverable, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-600">
                              <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* KPIs */}
                      <div className="space-y-2">
                        <h4 className="font-medium flex items-center text-gray-700">
                          <BarChart2 className="w-4 h-4 mr-2" />
                          KPIs
                        </h4>
                        <ul className="space-y-1">
                          {phase.kpis.map((kpi, idx) => (
                            <li key={idx} className="flex items-start text-sm text-gray-600">
                              <span className="text-purple-500 mr-2">•</span>
                              {kpi}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quality Gates */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-700 mb-4">Quality Gates</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {qualityGates.map((gate, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                  <h4 className="font-medium text-blue-600 mb-3 flex items-center">
                    <Database className="w-4 h-4 mr-2" />
                    {gate.phase}
                  </h4>
                  <ul className="space-y-2">
                    {gate.criteria.map((criterion, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        {criterion}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProductLifecycle;